import { useEffect, useState } from "react";
import { BarChart3 } from "lucide-react";
import api from "@/lib/api";

/** Lightweight inline bar chart — no external dep. */
function BarChart({ data, xLabel, valueKey = "count", labelKey = "label", testid }) {
  if (!data || data.length === 0) {
    return <div className="text-sm text-slate-400 italic" data-testid={`${testid}-empty`}>No data yet.</div>;
  }
  const max = Math.max(1, ...data.map((d) => d[valueKey] || 0));
  return (
    <div data-testid={testid} className="space-y-2">
      {data.map((d, i) => {
        const w = Math.round(((d[valueKey] || 0) / max) * 100);
        return (
          <div key={i} className="flex items-center gap-3 text-xs">
            <div className="w-24 text-slate-500 text-right truncate">{d[labelKey]}</div>
            <div className="flex-1 bg-slate-100 rounded-full h-3 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#1E3A8A] to-[#8B5CF6]"
                style={{ width: `${w}%` }}
              />
            </div>
            <div className="w-10 text-right font-semibold text-[#0F172A]">{d[valueKey] || 0}</div>
          </div>
        );
      })}
      {xLabel && <div className="pt-2 text-[10px] uppercase tracking-wider text-slate-400">{xLabel}</div>}
    </div>
  );
}

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/analytics")
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-sm text-[#475569]" data-testid="analytics-loading">Loading…</div>;
  if (!data) return <div className="text-sm text-[#475569]" data-testid="analytics-error">Failed to load analytics.</div>;

  const daily = data.daily_last_30.map((d) => ({ label: d.date.slice(5), count: d.count }));
  const weekly = data.weekly_last_12.map((d) => ({ label: d.week_of.slice(5), count: d.count }));
  const monthly = data.monthly_last_12.map((d) => ({ label: d.month, count: d.count }));
  const services = (data.most_requested_services || []).map((d) => ({ label: d.service, count: d.count }));

  return (
    <div data-testid="analytics-page">
      <h2 className="text-2xl font-display font-bold text-[#0F172A] flex items-center gap-2">
        <BarChart3 className="w-6 h-6 text-[#1E3A8A]" /> Website Analytics
      </h2>
      <p className="text-sm text-[#475569] mt-1">Estimate request trends pulled live from MongoDB.</p>

      <div className="mt-6 grid lg:grid-cols-2 gap-5">
        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total submissions</p>
          <p className="text-4xl font-display font-bold text-[#0F172A] mt-1" data-testid="analytics-total">{data.total_submissions}</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Most requested services</p>
          <BarChart data={services} testid="chart-services" />
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Daily leads — last 30 days</p>
          <BarChart data={daily} testid="chart-daily" />
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Weekly leads — last 12 weeks</p>
          <BarChart data={weekly} testid="chart-weekly" />
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-6 lg:col-span-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Monthly leads — last 12 months</p>
          <BarChart data={monthly} testid="chart-monthly" />
        </div>
      </div>
    </div>
  );
}
